/**
 * Structured log forwarding to CloudWatch via Lambda (POST).
 * Builds the event body from raw inputs and sends via the shared HTTP service,
 * so failures surface through the normal error path (toast + Sentry).
 */

import http from "./http/http";

type CloudWatchLevel = "debug" | "info" | "warning" | "error" | "fatal";

interface CloudWatchLogBase {
  timestamp: number;
  level: CloudWatchLevel;
  event_name: string;
  environment: string;
  app_version?: string;
  session_id?: string;
  test_type?: string;
  test_id?: string;
  duration_ms?: number;
  status_code?: number;
  data?: Record<string, unknown>;
  raw_message: string;
}

export interface CloudWatchLogEvent extends CloudWatchLogBase {
  event_type: "log";
}

export interface CloudWatchExceptionEvent extends CloudWatchLogBase {
  event_type: "exception";
  error_type: string;
  error_message: string;
  has_stack: boolean;
}

export type CloudWatchEvent = CloudWatchLogEvent | CloudWatchExceptionEvent;

const CLOUDWATCH_PROMOTED_TAG_KEYS = new Set<string>([
  "sessionId",
  "testType",
  "app_version",
  "event_type",
]);

const getCloudWatchEnvironment = (): string =>
  import.meta.env.VITE_SENTRY_ENVIRONMENT ||
  import.meta.env.MODE ||
  "development";

const filterCloudWatchData = (
  mergedTags: Record<string, string>,
  extra: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  const tagData: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(mergedTags)) {
    if (!CLOUDWATCH_PROMOTED_TAG_KEYS.has(key)) {
      tagData[key] = value;
    }
  }
  const merged = { ...tagData, ...extra };
  return Object.keys(merged).length > 0 ? merged : undefined;
};

export interface ForwardLogOptions {
  message: string;
  mergedTags: Record<string, string>;
  extra: Record<string, unknown>;
}

export interface ForwardExceptionOptions extends ForwardLogOptions {
  error: Error | unknown;
  errorObject: Error;
}

export const buildCloudWatchEventBody = (
  level: CloudWatchLevel,
  options: ForwardLogOptions | ForwardExceptionOptions,
): CloudWatchEvent => {
  const { message, mergedTags, extra } = options;
  const cwData = filterCloudWatchData(mergedTags, extra);

  const base: CloudWatchLogBase = {
    timestamp: Date.now(),
    level,
    event_name: mergedTags.event_type ?? message,
    environment: getCloudWatchEnvironment(),
    ...(mergedTags.app_version ? { app_version: mergedTags.app_version } : {}),
    ...(mergedTags.sessionId ? { session_id: mergedTags.sessionId } : {}),
    ...(mergedTags.testType ? { test_type: String(mergedTags.testType) } : {}),
    ...(cwData ? { data: cwData } : {}),
    raw_message: message,
  };

  if ("errorObject" in options) {
    const { error, errorObject } = options;
    return {
      ...base,
      event_type: "exception",
      error_type:
        error instanceof Error ? error.constructor.name : typeof error,
      error_message: errorObject.message,
      has_stack: !!errorObject.stack,
    };
  }

  return { ...base, event_type: "log" };
};

export const forwardLogToCloudWatch = (
  level: CloudWatchLevel,
  options: ForwardLogOptions | ForwardExceptionOptions,
): void => {
  const endpoint = import.meta.env.VITE_CLOUDWATCH_LOG_ENDPOINT?.trim();
  if (!endpoint) {
    return;
  }

  const event = buildCloudWatchEventBody(level, options);
  const apiKey = import.meta.env.VITE_AUTH_KEY;

  http
    .request({
      key: "sendCloudWatchLog" as any,
      data: event,
      extraHeader: apiKey ? { "X-API-Key": apiKey } : {},
    })
    .catch(() => {
      // Error already handled by HTTP interceptor (toast + Sentry).
      // This catch only prevents an unhandled promise rejection.
    });
};
